"use client";

import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { handleApiError, userAPI, postsAPI } from "../../../services/api";
import { resolveImageUrl, withPlaceholder, getUserPlaceholderImage } from "../../../utils/imageUtils";
import UserAverageRating from "../../social/UserAverageRating";
import "./SocialSidebar.css";

const SocialSidebar = ({ currentUser, userType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getProfileImageUrl, getUserType, user } = useAuth();

  const { t } = useTranslation();
  const fallbackName = currentUser?.name || t("socialSidebar.fallbackName");
  const fallbackRole = currentUser?.role || t("socialSidebar.fallbackRole");
  const [profileStats, setProfileStats] = useState(currentUser?.stats || null);
  const [profileSubType, setProfileSubType] = useState(currentUser?.sub_type || null);
  const [profileSubTypeDisplayName, setProfileSubTypeDisplayName] = useState(currentUser?.sub_type_display_name || null);
  const [averageRating, setAverageRating] = useState(currentUser?.average_rating ?? 0);
  const [ratingsCount, setRatingsCount] = useState(currentUser?.ratings_count ?? 0);
  const [ratingLabel, setRatingLabel] = useState(currentUser?.rating_label ?? null);

  // Derive counts for Posts and Connects
  const postsCount = Math.max(
    0,
    profileStats?.posts ??
    currentUser?.stats?.posts ??
    currentUser?.posts_count ??
    0
  );
  const connectsCount = Math.max(
    0,
    profileStats?.friends ??
    currentUser?.friends_count ??
    currentUser?.stats?.friends ??
    currentUser?.connections_count ??
    currentUser?.stats?.connects ??
    currentUser?.following_count ??
    currentUser?.followers_count ??
    0
  );
  const isChannelPage = location.pathname.startsWith("/social/channels");


  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B',           // Navy Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  // Get pet guardian subtype and display name
  // Check multiple possible locations for subtype data (profile API, user object, currentUser)
  const guardianSubType =
    profileSubType ||
    user?.sub_type ||
    user?.subType ||
    currentUser?.sub_type ||
    currentUser?.subType;

  const guardianSubTypeDisplayName =
    profileSubTypeDisplayName ||
    user?.sub_type_display_name ||
    user?.subTypeDisplayName ||
    currentUser?.sub_type_display_name ||
    currentUser?.subTypeDisplayName;

  // Get veterinarian subtype and display name (same fields, different user type)
  const vetSubType = guardianSubType; // Same data source, just different user type
  const vetSubTypeDisplayName = guardianSubTypeDisplayName; // Same data source

  const hasSubType = (currentUserType === 'PET_GUARDIAN' && guardianSubType) ||
    (currentUserType === 'VETERINARIAN' && vetSubType);

  // Format subtype display name (use display_name if available, otherwise capitalize sub_type)
  const getSubTypeDisplay = () => {
    if (!hasSubType) return null;

    // Use display name if available
    if (guardianSubTypeDisplayName || vetSubTypeDisplayName) {
      return guardianSubTypeDisplayName || vetSubTypeDisplayName;
    }

    // Fallback: capitalize first letter of sub_type
    const subType = guardianSubType || vetSubType;
    if (subType) {
      // Handle snake_case (e.g., clinic_owner -> Clinic Owner)
      return subType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return null;
  };

  const subTypeDisplay = getSubTypeDisplay();

  const handleNavigation = (path) => {
    navigate(path);
  };


  // Load fresh profile stats ONLY if not provided in props
  useEffect(() => {
    // If we already have the critical data, skip the redundant API call
    if (currentUser?.stats && currentUser?.average_rating !== undefined) {
      return;
    }

    let cancelled = false;

    const loadProfileStats = async () => {
      try {
        const response = await userAPI.getProfile({ params: { posts_page: 1, posts_per_page: 10 } });
        const payload = response.data?.data || response.data;
        if (!payload || cancelled) return;

        const friends =
          payload.friends_count ??
          payload.stats?.friends ??
          0;
        const posts =
          payload.posts_count ??
          payload.stats?.posts ??
          0;

        setProfileStats({
          friends,
          posts,
        });

        // Also store subtype data from profile API
        if (payload.sub_type) {
          setProfileSubType(payload.sub_type);
        }
        if (payload.sub_type_display_name) {
          setProfileSubTypeDisplayName(payload.sub_type_display_name);
        }

        // Store rating data from profile API
        let avgRating = payload.average_rating ?? payload.averageRating ?? 0;
        let countRating = payload.ratings_count ?? payload.ratingsCount ?? 0;

        // Ensure we have posts to calculate rating from if backend fails
        let postsData = payload.posts;

        // Fallback: If rating is missing/zero, check posts.
        // If posts are missing from payload, fetch them.
        if (avgRating === null || avgRating === 0 || countRating === 0) {
          if (!postsData || !Array.isArray(postsData) || postsData.length === 0) {
            try {
              // Fetch only if needed
              const postsResponse = await postsAPI.getMyPosts();
              const fetchedPosts = postsResponse.data?.data || postsResponse.data;
              if (Array.isArray(fetchedPosts)) {
                postsData = fetchedPosts;
              }
            } catch (err) {
              console.warn("SocialSidebar: Failed to fetch posts for rating calculation", err);
            }
          }

          // Now try calculation again with postsData
          if (postsData && Array.isArray(postsData) && postsData.length > 0) {
            const ratedPosts = postsData.filter(p =>
              (p.average_rating !== null && p.average_rating !== undefined && parseFloat(p.average_rating) > 0) ||
              (p.avg_rating !== null && p.avg_rating !== undefined && parseFloat(p.avg_rating) > 0)
            );

            if (ratedPosts.length > 0) {
              const totalRating = ratedPosts.reduce((sum, p) =>
                sum + (parseFloat(p.average_rating ?? p.avg_rating) || 0), 0
              );
              avgRating = totalRating / ratedPosts.length;
              countRating = ratedPosts.length;

            }
          }
        }

        setAverageRating(avgRating);
        setRatingsCount(countRating);
        setRatingLabel(payload.rating_label ?? payload.ratingLabel ?? null);
      } catch (error) {
        // Non-fatal: sidebar can fall back to existing currentUser stats
        console.warn("Failed to load sidebar profile stats:", error);
      }
    };

    loadProfileStats();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);



  return (
    <>
      {/* Desktop and Tablet Sidebar */}
      <div
        className={`social-sidebar-desktop${isChannelPage ? " channel-list-only" : ""}`}
      >
        {/* Profile Section */}
        {!isChannelPage && (
          <div className="profile-section-container">
            {/* Profile Card - Contains picture, name, username, posts, and connects */}
            <div className="profile-card" style={{}}>
              {/* Profile Info - Horizontal Layout */}
              <div className="profile-info-container">
                {/* Picture on the left */}
                <img
                  src={getProfileImageUrl() || getUserPlaceholderImage(user)}
                  alt={t("socialSidebar.profileImageAlt", { name: fallbackName })}
                  className="profile-picture"
                  style={{
                    border: `4px solid ${userTypeColor}`,
                  }}
                  onError={(e) => {
                    e.target.src = getUserPlaceholderImage(user);
                  }}
                />

                {/* Name and Username on the right */}
                <div className="profile-text-container">
                  <h3 className="profile-name">
                    {fallbackName}
                  </h3>

                  {/* Show rating under name if user is NOT a rescuer */}
                  {profileSubType !== 'rescuer' &&
                    subTypeDisplay?.toLowerCase() !== 'rescuer' &&
                    averageRating !== null && averageRating !== undefined && (
                      <UserAverageRating
                        averageRating={averageRating}
                        ratingsCount={ratingsCount}
                        ratingLabel={null}
                        size="small"
                      />
                    )}

                  {/* Subtype Tag - Between name and username */}
                  {subTypeDisplay && (
                    <span style={{
                      fontSize: '12px',
                      color: userTypeColor,
                      fontWeight: 600,
                      margin: '2px 0'
                    }}>
                      {subTypeDisplay}
                    </span>
                  )}

                  {/* Show rating under subtype if user IS a rescuer */}
                  {(profileSubType === 'rescuer' || subTypeDisplay?.toLowerCase() === 'rescuer') &&
                    averageRating !== null && averageRating !== undefined && (
                      <UserAverageRating
                        averageRating={averageRating}
                        ratingsCount={ratingsCount}
                        ratingLabel={null}
                        size="small"
                      />
                    )}
                </div>
              </div>

              {/* Stats: Posts + unified Connects (mutual connections/friends) */}
              <div className="profile-stats-container">
                <div className="profile-stat-item">
                  <p className="profile-stat-number">
                    {postsCount}
                  </p>
                  <span className="profile-stat-label">
                    {t("sideBar.Posts")}
                  </span>
                </div>

                <div className="profile-stat-item">
                  <p className="profile-stat-number">
                    {connectsCount}
                  </p>
                  <span className="profile-stat-label">
                    {t("sideBar.following")}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions Card - Contains My Profile and My Pets buttons */}
            <div className="actions-card" style={{ marginTop: "16px" }}>
              {/* Profile Button */}
              <button
                onClick={() => handleNavigation(`/social/profile`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                }}
              >
                {t("sideBar.myProfile")}
              </button>

              {/* My Pets Button */}
              <button
                onClick={() => handleNavigation(`/dashboard/pets`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.95858 2.50008C7.94192 2.35842 8.98358 3.43342 9.28358 4.91675C9.58358 6.39175 9.04191 7.70842 8.05858 7.85842C7.08358 8.00842 6.03358 6.93342 5.72525 5.45008C5.41691 3.97508 5.97525 2.65842 6.95858 2.50008ZM12.9169 2.50008C13.9086 2.65842 14.4586 3.97508 14.1669 5.45008C13.8502 6.93342 12.8086 8.00842 11.8252 7.85842C10.8336 7.70842 10.2919 6.39175 10.6002 4.91675C10.9002 3.43342 11.9419 2.35842 12.9169 2.50008ZM2.50025 6.33342C3.45025 5.92508 4.74191 6.66675 5.41691 7.95842C6.05025 9.27508 5.83358 10.6584 4.89191 11.0668C3.95025 11.4751 2.66691 10.7418 2.00858 9.43342C1.35025 8.12508 1.58358 6.73342 2.50025 6.33342ZM17.5002 6.33342C18.4169 6.73342 18.6502 8.12508 17.9919 9.43342C17.3336 10.7418 16.0502 11.4751 15.1086 11.0668C14.1669 10.6584 13.9502 9.27508 14.5836 7.95842C15.2586 6.66675 16.5502 5.92508 17.5002 6.33342ZM16.1086 15.3168C16.1419 16.1001 15.5419 16.9668 14.8252 17.2918C13.3336 17.9751 11.5669 16.5584 9.90858 16.5584C8.25025 16.5584 6.46691 18.0334 5.00025 17.2918C4.16691 16.8834 3.59191 15.8001 3.70025 14.9001C3.85025 13.6584 5.34191 12.9918 6.22525 12.0834C7.40025 10.9084 8.23358 8.70008 9.90858 8.70008C11.5752 8.70008 12.4586 10.8751 13.5836 12.0834C14.5086 13.1001 16.0502 13.9584 16.1086 15.3168Z"
                    fill="#FFFFFF"
                  />
                </svg>
                {t("navbar.My_Pets")}
              </button>

              {/* Communities Button */}
              {!isChannelPage && (
                <button
                  onClick={() => handleNavigation("/social/channels")}
                  className="action-button"
                  style={{
                    backgroundColor: userTypeColor,
                    border: 'none',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '16px',
                    height: '48px',
                    padding: '14px 20px',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                  </svg>
                  {t("sideBar.communities")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Sidebar - Hidden by default, shown via toggle */}
      <div
        className="social-sidebar-mobile"
        style={{
          display: "none",
          position: "fixed",
          top: "240px",
          left: "-100%",
          width: "280px",
          height: "calc(100vh - 240px)",
          background: "#FFFFFF",
          borderRight: "1px solid #eee",
          padding: "20px",
          overflowY: "auto",
          fontFamily: '"Red Hat Display", sans-serif',
          zIndex: 200,
          transition: "left 0.3s ease",
        }}
      >
        {/* Mobile Profile Section */}
        {!isChannelPage && (
          <div className="profile-section-container-mobile">
            {/* Profile Card - Contains picture, name, username, posts, and connects */}
            <div className="profile-card" style={{ borderColor: userTypeColor }}>
              {/* Profile Info - Horizontal Layout */}
              <div className="profile-info-container">
                {/* Picture on the left */}
                <img
                  src={getProfileImageUrl() || getUserPlaceholderImage(user)}
                  alt={t("socialSidebar.profileImageAlt", { name: fallbackName })}
                  className="profile-picture"
                  style={{
                    border: `4px solid ${userTypeColor}`,
                  }}
                  onError={(e) => {
                    e.target.src = getUserPlaceholderImage(user);
                  }}
                />

                {/* Name and Username on the right */}
                <div className="profile-text-container">
                  <h3 className="profile-name-mobile">
                    {fallbackName}
                  </h3>

                  {/* Subtype Tag - Between name and username */}
                  {subTypeDisplay && (
                    <span style={{
                      fontSize: '12px',
                      color: userTypeColor,
                      fontWeight: 600,
                      margin: '2px 0'
                    }}>
                      {subTypeDisplay}
                    </span>
                  )}
                </div>
              </div>

              {/* Stats: Posts + unified Connects (mutual connections/friends) */}
              <div className="profile-stats-container">
                <div className="profile-stat-item">
                  <p className="profile-stat-number-mobile">
                    {postsCount}
                  </p>
                  <span className="profile-stat-label-mobile">
                    {t("sideBar.Posts")}
                  </span>
                </div>

                <div className="profile-stat-item">
                  <p className="profile-stat-number-mobile">
                    {connectsCount}
                  </p>
                  <span className="profile-stat-label-mobile">
                    {t("sideBar.following")}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions Card - Contains My Profile and My Pets buttons */}
            <div className="actions-card" style={{ marginTop: "16px" }}>
              {/* Profile Button */}
              <button
                onClick={() => handleNavigation(`/social/profile`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                }}
              >
                {t("sideBar.myProfile")}
              </button>

              {/* My Pets Button */}
              <button
                onClick={() => handleNavigation(`/dashboard/pets`)}
                className="action-button"
                style={{
                  backgroundColor: userTypeColor,
                  border: 'none',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '16px',
                  height: '48px',
                  padding: '14px 20px',
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.95858 2.50008C7.94192 2.35842 8.98358 3.43342 9.28358 4.91675C9.58358 6.39175 9.04191 7.70842 8.05858 7.85842C7.08358 8.00842 6.03358 6.93342 5.72525 5.45008C5.41691 3.97508 5.97525 2.65842 6.95858 2.50008ZM12.9169 2.50008C13.9086 2.65842 14.4586 3.97508 14.1669 5.45008C13.8502 6.93342 12.8086 8.00842 11.8252 7.85842C10.8336 7.70842 10.2919 6.39175 10.6002 4.91675C10.9002 3.43342 11.9419 2.35842 12.9169 2.50008ZM2.50025 6.33342C3.45025 5.92508 4.74191 6.66675 5.41691 7.95842C6.05025 9.27508 5.83358 10.6584 4.89191 11.0668C3.95025 11.4751 2.66691 10.7418 2.00858 9.43342C1.35025 8.12508 1.58358 6.73342 2.50025 6.33342ZM17.5002 6.33342C18.4169 6.73342 18.6502 8.12508 17.9919 9.43342C17.3336 10.7418 16.0502 11.4751 15.1086 11.0668C14.1669 10.6584 13.9502 9.27508 14.5836 7.95842C15.2586 6.66675 16.5502 5.92508 17.5002 6.33342ZM16.1086 15.3168C16.1419 16.1001 15.5419 16.9668 14.8252 17.2918C13.3336 17.9751 11.5669 16.5584 9.90858 16.5584C8.25025 16.5584 6.46691 18.0334 5.00025 17.2918C4.16691 16.8834 3.59191 15.8001 3.70025 14.9001C3.85025 13.6584 5.34191 12.9918 6.22525 12.0834C7.40025 10.9084 8.23358 8.70008 9.90858 8.70008C11.5752 8.70008 12.4586 10.8751 13.5836 12.0834C14.5086 13.1001 16.0502 13.9584 16.1086 15.3168Z"
                    fill="#FFFFFF"
                  />
                </svg>
                {t("navbar.My_Pets")}
              </button>

              {/* Communities Button */}
              {!isChannelPage && (
                <button
                  onClick={() => handleNavigation("/social/channels")}
                  className="action-button"
                  style={{
                    backgroundColor: userTypeColor,
                    border: 'none',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '16px',
                    height: '48px',
                    padding: '14px 20px',
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                  </svg>
                  {t("sideBar.communities")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SocialSidebar;